import React, { useState, useEffect } from "react";
import {
  IconButton,
  Button,
  Typography,
  Tooltip,
  Menu,
  Badge,
  Box,
  CircularProgress,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { Link } from "react-router-dom";
import config from "../../../../config";

const Notifications = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    if (notifications.length === 0) {
      fetchNotifications();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const baseUrl = config.API_BASE_URL_V1;
      const response = await fetch(`${baseUrl}/notifications/latest?limit=3`, {
        headers: config.getHeaders()
      });
      const result = await response.json();

      if (result.success && result.data) {
        setNotifications(result.data);
        const unread = result.data.filter(n => n.status === 'unread').length;
        setUnreadCount(unread);
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch notifications on component mount
    fetchNotifications();

    // Optionally refresh every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <Tooltip title="Notification">
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{
            width: "35px",
            height: "35px",
            p: 0,
          }}
          aria-controls={open ? "account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          className="for-dark-notification"
        >
          <Badge
            badgeContent={unreadCount}
            color="error"
            sx={{ position: "absolute", top: "12px", right: "12px" }}
          ></Badge>
          <NotificationsNoneIcon color="action" sx={{ fontSize: "24px" }} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            padding: "0",
            borderRadius: "7px",
            boxShadow: "0 4px 45px #0000001a",
            overflow: "visible",
            mt: 1.5,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "5px",
            padding: "12px 20px",
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: "600", fontSize: "14px" }}>
            Notifications <span className="text-body">({notifications.length})</span>
          </Typography>
        </Box>

        <Box>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ padding: "20px", textAlign: "center" }}>
              <Typography sx={{ fontSize: "12px", color: "#999" }}>
                No notifications
              </Typography>
            </Box>
          ) : (
            notifications.map((notif) => (
              <Link
                key={notif.notification_id}
                to="/notifications"
                className="text-body"
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Box
                  className="border-top"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: "10px", sm: "15px" },
                    padding: "12px 20px",
                    backgroundColor: notif.status === 'unread' ? '#f0f4ff' : 'transparent',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ mb: "4px" }} className="text-black">
                      <strong style={{ fontWeight: "600" }}>
                        {notif.notification_title}
                      </strong>
                      {notif.status === 'unread' && (
                        <span style={{ marginLeft: '8px', color: '#6A5BFF', fontSize: '10px' }}>●</span>
                      )}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#666" }}>
                      {notif.notification_detail?.substring(0, 80)}...
                    </Typography>
                    <Typography sx={{ fontSize: "10px", color: "#999", mt: "4px" }}>
                      {new Date(notif.created_date).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Box>
              </Link>
            ))
          )}

          <Box className="border-top" textAlign="center">
            <Link
              to="/notifications"
              style={{
                fontWeight: "500",
                marginTop: "15px",
                marginBottom: "10px",
                display: "inline-block",
                textDecoration: "none",
                color: "#6A5BFF",
              }}
            >
              See All Notifications
            </Link>
          </Box>
        </Box>
      </Menu>
    </>
  );
};

export default Notifications;
